import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useDebouncedAction } from "../../../../../../helpers/commonFunction";
import { AppState } from "../../../../../../stateManager/store";
import { FormCreationData } from "../../../../../../stateManager/store/formCreation_v2/types";
import { getValue, getComponents } from "../../helpers";
import { data, getFlexDistributeOptions } from "../data";
import { Label, Wrapper, Dropdown } from "../Panel";
import { Checkbox } from "./helpers/constants";

import { onChange } from "./helpers/on-change"

const DEFAULT_FLEX_DIRECTION :string = 'properties.styles.$deviceType.flexDirection';
const DEFAULT_FLEX_DISTRIBUTE: string = "properties.styles.$deviceType.flexDistribute";
const DEFAULT_FLEX_ALIGN: string = "properties.styles.$deviceType.flexAlign";
const DEFAULT_FLEX_WRAP: string = "properties.styles.$deviceType.flexWrap";
const DEFAULT_FLEX_GROW: string = "properties.styles.$deviceType.flexGrow";
const DEFAULT_FLEX_SHRINK: string = "properties.styles.$deviceType.flexShrink";

type Props = {
  label?: string;
  isCheckedIn: boolean;
  defaultValue?: string;
}

type CheckboxProps = {
  isCheckedIn: boolean;
  defaultValue?: boolean;
}

export const FlexDistribute = (props: Props) => {
  const { components } = getComponents()
  const { selectedElementId, deviceType } = useSelector((state: AppState) => state.formJson)
  const { isCheckedIn, defaultValue } = props;
  const [oldValue, setOldValue] = useState<string>("");
  const [value, setValue] = useState(defaultValue);
  const component: FormCreationData = components[selectedElementId];
  const componentDeviceData = component?.properties?.styles[deviceType]

  const distributionType: "Row" | "Column" = component.type === "Container" ? componentDeviceData?.flexDirection === "row" ? "Row" : "Column" : component.type === "Row" ? "Row" : "Column";
  const mainOptions = getFlexDistributeOptions(distributionType, "main").map(el => ({ name: el.label, value: el.value }))

  useEffect(() => {
    if (component) {
      const previousValue = getValue(component, DEFAULT_FLEX_DISTRIBUTE.split("."), deviceType);
      const val = mainOptions.find(item => item.value === previousValue)?.name || mainOptions[0].name;
      if (val) {
        setValue(val)
        setOldValue(val)
      }
    }
    // eslint-disable-next-line
  }, [getValue(component, DEFAULT_FLEX_DISTRIBUTE.split("."), deviceType)]);

  return (
    <Wrapper>
      <Label id="flexDistribute" label="Distribute" />
      <Dropdown
        cursorStyle={!isCheckedIn ? "not-allowed" : "auto"}
        disabled={!isCheckedIn}
        options={mainOptions}
        selected={{ name: value as string }}
        onChange={({ value }) => {
          setValue(value)
          onChange({
            path: DEFAULT_FLEX_DISTRIBUTE,
            value,
            oldValue,
            setOldValue,
            selectedElementId
          })
        }}
      />
    </Wrapper>
  )
};

export const FlexAlign = (props: Props) => {
  const { components } = getComponents()
  const { selectedElementId, deviceType } = useSelector((state: AppState) => state.formJson)
  const { isCheckedIn, defaultValue } = props;
  const [oldValue, setOldValue] = useState<string>("");
  const [value, setValue] = useState(defaultValue);
  const component = components[selectedElementId];
  const componentDeviceData = component?.properties?.styles[deviceType]

  const distributionType: "Row" | "Column" = component.type === "Container" ? componentDeviceData?.flexDirection === "row" ? "Row" : "Column" : component.type === "Row" ? "Row" : "Column";
  const crossOptions = getFlexDistributeOptions(distributionType, "cross").map(el => ({ name: el.label, value: el.value }))

  useEffect(() => {
    if (component) {
      const previousValue = getValue(component, DEFAULT_FLEX_ALIGN.split("."), deviceType);
      const val = crossOptions.find(item => item.value === previousValue)?.name || crossOptions[0].name;
      setValue(val)
      setOldValue(val)
    }
    // eslint-disable-next-line
  }, [getValue(component, DEFAULT_FLEX_ALIGN.split("."), deviceType)]);

  return (
    <Wrapper>
      <Label id="flexAlign" label="Align" />
      <Dropdown
        cursorStyle={!isCheckedIn ? "not-allowed" : "auto"}
        disabled={!isCheckedIn}
        options={crossOptions}
        selected={{ name: value as string }}
        onChange={({ value }) => {
          setValue(value)
          onChange({
            path: DEFAULT_FLEX_ALIGN,
            value,
            oldValue,
            setOldValue,
            selectedElementId
          })
        }}
      />
    </Wrapper>
  )
};


export const FlexWrap = (props: CheckboxProps) => {
  const { components } = getComponents()
  const { selectedElementId, deviceType } = useSelector((state: AppState) => state.formJson)
  const { isCheckedIn, defaultValue } = props;
  const [oldValue, setOldValue] = useState(Boolean);
  const [value, setValue] = useState(defaultValue);
  const component = components[selectedElementId];

  useEffect(() => {
    if (component) {
      const previousValue = getValue(component, DEFAULT_FLEX_WRAP.split("."), deviceType);
      setValue(previousValue)
      setOldValue(previousValue)
    }
    // eslint-disable-next-line
  }, [getValue(component, DEFAULT_FLEX_WRAP.split("."), deviceType)]);

  return (
    <Checkbox
      disabled={!isCheckedIn}
      key="flexWrap"
      id="flexWrap"
      value={value ? value : false}
      label="Wrap on overflow"
      onChange={(_, value: boolean) => {
        setValue(value);
        onChange({
          path: DEFAULT_FLEX_WRAP,
          value,
          oldValue,
          setOldValue,
          selectedElementId
        })
      }}
    />
  )
};


export const FlexGrow = (props: Props) => {
  const { components } = getComponents()
  const { selectedElementId, deviceType } = useSelector((state: AppState) => state.formJson)
  const { isCheckedIn, defaultValue, label } = props;
  const [oldValue, setOldValue] = useState<string>("");
  const [value, setValue] = useDebouncedAction(defaultValue, 500 || "");
  const component = components[selectedElementId];

  useEffect(() => {
    if (component) {
      const previousValue = getValue(component, DEFAULT_FLEX_GROW.split("."), deviceType);
      setValue(previousValue)
      setOldValue(previousValue)
    }
    // eslint-disable-next-line
  }, [getValue(component, DEFAULT_FLEX_GROW.split("."), deviceType) ?? 0]);

  return (
    <div className="panel-div">
      <label
        htmlFor="flexGrow"
        className="panel-label"
      >
        {label}
      </label>
      <input
        className="panel-input"
        style={{ cursor: !isCheckedIn ? "not-allowed" : "auto"}}
        disabled={!isCheckedIn}
        type="number"
        min="0"
        id="flexGrow"
        value={value}
        onChange={e => {
          const changeValue = e.target.value;
          const value = changeValue ? Number(changeValue) > 0 ? changeValue : "0": changeValue;
          setValue(value, () => onChange({
            path: DEFAULT_FLEX_GROW,
            value,
            oldValue,
            setOldValue,
            selectedElementId
          }))
        }}
       
      />
    </div>
  )
};
export const FlexShrink = (props: Props) => {
  const { components } = getComponents()
  const { selectedElementId, deviceType } = useSelector((state: AppState) => state.formJson)
  const { isCheckedIn, defaultValue, label } = props;
  const [oldValue, setOldValue] = useState<string>("");
  const [value, setValue] = useDebouncedAction(defaultValue, 500 || "");
  const component = components[selectedElementId];

  useEffect(() => {
    if (component) {
      const previousValue = getValue(component, DEFAULT_FLEX_SHRINK.split("."), deviceType);
      setValue(previousValue)
      setOldValue(previousValue)
    }
    // eslint-disable-next-line
  }, [getValue(component, DEFAULT_FLEX_SHRINK.split("."), deviceType) ?? 0]);

  return (
    <div className="panel-div">
      <label
        htmlFor="flexShrink"
        className="panel-label"
      >
        {label}
      </label>
      <input
        className="panel-input"
        style={{ cursor: !isCheckedIn ? "not-allowed" : "auto" }}
        disabled={!isCheckedIn}
        type="number"
        min="0"
        id="flexShrink"
        value={value}
        onChange={e => {
          const changeValue = e.target.value;
          const value = changeValue ? Number(changeValue) > 0 ? changeValue : "0": changeValue;
          setValue(value, () => onChange({
            path: DEFAULT_FLEX_SHRINK,
            value,
            oldValue,
            setOldValue,
            selectedElementId
          }))
        }}
      />
    </div>
  )
};

export const FlexDirection = (props: Props) => {
  const { components } = getComponents()
  const { selectedElementId, deviceType } = useSelector((state: AppState) => state.formJson)
  const { isCheckedIn, defaultValue } = props;
  const [oldValue, setOldValue] = useState<string>("");
  const [value, setValue] = useState(defaultValue);
  const component = components[selectedElementId];

  const crossOptions = data.flexDirection.map(el => ({ name: el.label, value: el.value }))

  useEffect(() => {
    if (component) {
      const previousValue = getValue(component, DEFAULT_FLEX_DIRECTION.split("."), deviceType);
      const val = crossOptions.find(item => item.value === previousValue)?.name || crossOptions[1].name;
      setValue(val)
      setOldValue(val)
    }
    // eslint-disable-next-line
  }, [getValue(component, DEFAULT_FLEX_DIRECTION.split("."), deviceType)]);

  useEffect(() => {
    if (defaultValue === undefined) {
      setValue("Column")
    }
  }, [defaultValue])

  return (
    <Wrapper>
      <Label id="flexDirection" label="Flex Direction" />
      <Dropdown
        cursorStyle={!isCheckedIn ? "not-allowed" : "auto"}
        disabled={!isCheckedIn}
        options={crossOptions}
        selected={{ name: value as string }}
        onChange={({ value }) => {
          setValue(value)
          onChange({
            path: DEFAULT_FLEX_DIRECTION,
            value,
            oldValue,
            setOldValue,
            selectedElementId
          })
        }}
      />
    </Wrapper>
  )
};